import { useCallback } from 'react';
import { openLink } from '@tma.js/sdk-react';

import { useTrackViewMutation } from '@/services/newsApi';

/**
 * Article share hook for the Telegram Mini App.
 *
 * Mobile-app uses native `Share.share()` for the system share sheet. In the
 * Telegram WebView the closest equivalent is `t.me/share/url`, which Telegram
 * routes through the user's Telegram contacts share sheet — exactly the
 * audience that matters for a Telegram bot.
 *
 * We open it via `openLink.ifAvailable` so the WebView delegates to the
 * Telegram client's native share UI (or the system browser as a fallback).
 *
 * Note: deliberately no Firebase Dynamic Links — the public web canonical
 * URL `https://tma.qlash.gg/news/{slug}` is enough; the Telegram link
 * preview will pick up Open Graph from there once we add it.
 */
export function useShareArticle(articleId: string, slug: string, title: string) {
  const [trackView] = useTrackViewMutation();

  return useCallback(() => {
    const url = `https://tma.qlash.gg/news/${slug}`;
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
    const opened = openLink.ifAvailable(shareUrl);
    // Telemetry: tag the article as shared whether or not the share UI
    // actually completed (Telegram doesn't surface that). Best-effort.
    trackView({ articleId, shared: true }).catch(() => {});
    if (!opened) {
      // Outside Telegram: copy URL to clipboard as a graceful fallback.
      try {
        void navigator.clipboard.writeText(url);
      } catch {
        /* clipboard unavailable */
      }
    }
  }, [articleId, slug, title, trackView]);
}
